import { ZCDDataVisualizationColor } from '@zencity/common-ui';

export const concernsColorsOrder: (keyof typeof ZCDDataVisualizationColor)[] = [
  'ZCD_DATA_BLUE_70',
  'ZCD_DATA_ORANGE_40',
  'ZCD_DATA_CYAN_50',
  'ZCD_DATA_YELLOW_20',
  'ZCD_DATA_ROSE_60',
  'ZCD_DATA_ROSE_20',
  'ZCD_DATA_GREEN_10',
  'ZCD_DATA_PURPLE_50',
  'ZCD_DATA_BLUE_20',
  'ZCD_DATA_RED_50',
  'ZCD_DATA_YELLOW_50',
  'ZCD_DATA_CYAN_20',
];

export const concernsBreakdownColorsOrder: (keyof typeof ZCDDataVisualizationColor)[][] = [
  ['ZCD_DATA_BLUE_70', 'ZCD_DATA_BLUE_60', 'ZCD_DATA_BLUE_40', 'ZCD_DATA_BLUE_20', 'ZCD_DATA_BLUE_5'],
  ['ZCD_DATA_ORANGE_40', 'ZCD_DATA_ORANGE_30', 'ZCD_DATA_ORANGE_20', 'ZCD_DATA_ORANGE_10', 'ZCD_DATA_ORANGE_5'],
  ['ZCD_DATA_CYAN_50', 'ZCD_DATA_CYAN_40', 'ZCD_DATA_CYAN_30', 'ZCD_DATA_CYAN_10', 'ZCD_DATA_CYAN_5'],
];

interface BreakdownBySubTopics {
  [subTopicId: string]: {
    sub_topic_id: number;
    sub_topic_value: string;
    sub_topic_label: string;
    total_classifications: number;
  };
}

interface BreakdownByTopics {
  [topicId: string]: {
    topic_id: number;
    topic_value: string;
    topic_label: string;
    total_classifications: number;
    sub_topics: BreakdownBySubTopics;
  };
}

interface BreakdownByDivisionsByTopics {
  [divisionId: string]: {
    division_id: number;
    division_depth: number;
    polygon_id: string;
    polygon_name: string;
    total_classifications: number;
    topics: BreakdownByTopics;
  };
}

export interface VaultAnswerClassificationSubTopicBreakdownResponse {
  total_responses: number;
  total_classifications: number;
  divisions: BreakdownByDivisionsByTopics;
}

/**
 * Aggregate the data, removing the "divisions" breakdown.
 */
const aggregateByTopics = (rawData: VaultAnswerClassificationSubTopicBreakdownResponse): BreakdownByTopics => {
  const aggregatedData = {} as BreakdownByTopics;

  Object.values(rawData.divisions).forEach((divisionData) => {
    Object.values(divisionData.topics).forEach((topicData) => {
      if (!Object.hasOwn(aggregatedData, topicData.topic_id)) {
        aggregatedData[topicData.topic_id] = { ...topicData, total_classifications: 0, sub_topics: {} };
      }

      // eslint-disable-next-line max-nested-callbacks
      Object.values(topicData.sub_topics).forEach((subTopicData) => {
        if (!Object.hasOwn(aggregatedData[topicData.topic_id].sub_topics, subTopicData.sub_topic_id)) {
          aggregatedData[topicData.topic_id].sub_topics[subTopicData.sub_topic_id] = {
            ...subTopicData,
            total_classifications: 0,
          };
        }

        aggregatedData[topicData.topic_id].total_classifications += subTopicData.total_classifications;
        aggregatedData[topicData.topic_id].sub_topics[subTopicData.sub_topic_id].total_classifications +=
          subTopicData.total_classifications;
      });
    });
  });

  return aggregatedData;
};

export interface ConcernItem {
  id: number;
  label: string;
  total: number;
  percentage: number;
  previousTotal?: number;
  previousPercentage?: number;
}

export interface SortedTopConcern extends ConcernItem {
  subTopics: ConcernItem[];
}

const descSortByTotal = (itemA: ConcernItem, itemB: ConcernItem) => itemB.total - itemA.total;
const MINIMAL_FOR_DISPLAY_THRESHOLD = 0.5;
/**
 * Extract the top concerns sorted by its count, descending.
 */
export const extractSortedTopConcerns = (
  rawData: VaultAnswerClassificationSubTopicBreakdownResponse,
  previousCycleTopConcerns?: SortedTopConcern[],
): SortedTopConcern[] => {
  const byTopics = aggregateByTopics(rawData);

  let previousCycleTopConcernsById: Record<number, SortedTopConcern> = {};
  if (previousCycleTopConcerns && previousCycleTopConcerns?.length > 0) {
    previousCycleTopConcernsById = previousCycleTopConcerns.reduce(
      (accum, item) => ({ ...accum, [item.id]: item }),
      {},
    );
  }

  return Object.values(byTopics)
    .map((topicData) => {
      const subTopics = Object.values(topicData.sub_topics)
        .map((subTopicData) => ({
          id: subTopicData.sub_topic_id,
          label: subTopicData.sub_topic_label,
          total: subTopicData.total_classifications,
          percentage:
            topicData.total_classifications > 0
              ? (100 * subTopicData.total_classifications) / topicData.total_classifications
              : 0,
        }))
        .sort(descSortByTotal);

      return {
        id: topicData.topic_id,
        label: topicData.topic_label,
        total: topicData.total_classifications,
        percentage:
          rawData.total_classifications > 0
            ? (100 * topicData.total_classifications) / rawData.total_classifications
            : 0,
        previousTotal: previousCycleTopConcernsById[topicData.topic_id]?.total,
        previousPercentage: previousCycleTopConcernsById[topicData.topic_id]?.percentage,
        subTopics,
      };
    })
    .filter((concern) => concern.percentage >= MINIMAL_FOR_DISPLAY_THRESHOLD)
    .sort(descSortByTotal);
};

/**
 * A division ID is always a positive integer.
 */
export const filterOutMissingDivision = (divisionData: { division_id: number }) => divisionData.division_id > 0;

export const sortByPolygonName = (divisionDataA: { polygon_name: string }, divisionDataB: { polygon_name: string }) =>
  divisionDataA.polygon_name > divisionDataB.polygon_name ? 1 : -1;

export const sortByTotalClassifications = (
  itemA: { total_classifications: number },
  itemB: { total_classifications: number },
) => itemB.total_classifications - itemA.total_classifications;
